/**
 * Info affordance for one feedback category: a small "i" badge that reveals
 * the category's description and examples on hover.
 *
 * Hover-only (CSS `group-hover`) so it needs no state and stays in sync with
 * the row it belongs to. The card opens upward and right-aligned because the
 * feedback form sits at the bottom of a question card whose collapsible
 * wrapper clips overflow — anything opening downward or outward would be cut.
 *
 * @module components/questionnaire/feedbackCategoryInfoIcon
 */
import { InfoIcon } from "../../svg/info-icon";
import { FeedbackCategory } from "./feedbackCategories";

/**
 * Props for the category info icon.
 */
interface FeedbackCategoryInfoIconProps {
  /** Category whose description / includes copy the tooltip shows. */
  category: FeedbackCategory;
}

/**
 * Renders the "i" badge and its hover card.
 *
 * @param props - Category copy shown inside the hover card
 * @returns Info icon with hover card
 */
const FeedbackCategoryInfoIcon = ({
  category,
}: FeedbackCategoryInfoIconProps) => (
  <span className="group relative flex shrink-0 items-center">
    <span role="img" aria-label={`About ${category.label}`} className="flex">
      <InfoIcon width="16" height="16" stroke="#8C8C8C" />
    </span>
    {/*
      The wrapper is flush against the icon (`bottom-full`) and owns the visual
      gap as padding, so the pointer never leaves the group on its way to the
      card — otherwise the card would vanish before it could be scrolled.
    */}
    <div className="invisible absolute bottom-full right-0 z-50 pb-2 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100">
      <div className="max-h-60 w-80 overflow-y-auto overscroll-contain rounded-lg border border-[#E5E5E5] bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <p className="m-0 text-xs font-semibold leading-4 text-[#0F0F0F]">
          Description
        </p>
        <p className="mb-3 mt-1 text-xs font-normal leading-[1.125rem] text-[#2E2E2E]">
          {category.description}
        </p>
        <p className="m-0 text-xs font-semibold leading-4 text-[#0F0F0F]">
          Includes
        </p>
        <p className="mt-1 text-xs font-normal leading-[1.125rem] text-[#2E2E2E]">
          {category.includes}
        </p>
      </div>
    </div>
  </span>
);

export default FeedbackCategoryInfoIcon;
